import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown
} from 'reactstrap'

import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { dataSlice } from '../../../redux/slices'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { getFileSize } from '../../../utils/helpers'
import { DataStateVersions } from '../../../redux/slices'
import { DeleteModal } from '../Modals/DeleteSelectionModal/DeleteSelectionModal'
import DownloadModal from '../Modals/DownloadModal/DownloadModal'
import ExportSelectionModal from '../Modals/ExportSelectionModal/ExportSelectionModal'
import CheckoutSelectionModal from '../Modals/CheckoutSelectionsModal/CheckoutSelectionModal'
import { searchDataThunk } from '../../../redux/slices'
import { selectDataSelectionMode } from '../../../redux/slices/dataSlice'

type ModalType = 'delete' | 'download' | 'export' | 'checkout'

export const ResultsActionSelector: React.FC = () => {
  const versions = useAppSelector((state) => state.data.versions)
  const { hasLocalSelected, hasNonLocalSelected } = useAppSelector(
    selectDataSelectionMode
  )
  const dispatch = useDispatch()

  const { count, size } = useMemo(() => getSelectedCount(versions), [versions])

  // Single modal state instead of separate objects
  const [activeModal, setActiveModal] = useState<ModalType | null>(null)

  const handleOpenModal = (type: ModalType) => {
    if (count > 0) setActiveModal(type)
  }

  const handleClearSelections = () => {
    setActiveModal(null)
    dispatch(dataSlice.actions.clearAllSelections()) // Clear selections on close
  }

  const handleDeleteClose = (deleteCompleted: boolean) => {
    setActiveModal(null)
    if (deleteCompleted) {
      dispatch(searchDataThunk()) // Only trigger search if delete happened
    }
  }

  const onlyNonLocal = hasNonLocalSelected && !hasLocalSelected
  const mixedSelection = hasLocalSelected && hasNonLocalSelected

  const menuItems: MenuItem[] = (() => {
    if (mixedSelection) {
      // Fallback to most restrictive: allow only clearing when state is inconsistent
      return [
        {
          text: 'Clear Selections',
          icon: 'glyphicons glyphicons-remove',
          onClick: handleClearSelections
        }
      ]
    }

    if (onlyNonLocal) {
      // Archived (non-local) selections: checkout + clear only
      return [
        {
          text: 'Checkout Selections',
          icon: 'glyphicons glyphicons-cloud-download',
          onClick: () => handleOpenModal('checkout')
        },
        {
          text: 'Clear Selections',
          icon: 'glyphicons glyphicons-remove',
          onClick: handleClearSelections
        }
      ]
    }

    // Local selections or no selection: full set except checkout
    return [
      {
        text: 'Create Download',
        icon: 'glyphicons glyphicons-compressed',
        onClick: () => handleOpenModal('download')
      },
      {
        text: 'Export Selections',
        icon: 'glyphicons glyphicons-restart',
        onClick: () => handleOpenModal('export')
      },
      {
        text: 'Move to Recycling',
        icon: 'glyphicons glyphicons-bin',
        onClick: () => handleOpenModal('delete')
      },
      {
        text: 'Clear Selections',
        icon: 'glyphicons glyphicons-remove',
        onClick: handleClearSelections
      }
    ]
  })()

  return (
    <>
      {activeModal === 'download' && (
        <DownloadModal onClose={handleClearSelections} />
      )}
      {activeModal === 'delete' && <DeleteModal onClose={handleDeleteClose} />}
      {activeModal === 'export' && (
        <ExportSelectionModal onClose={handleClearSelections} />
      )}
      {activeModal === 'checkout' && (
        <CheckoutSelectionModal onClose={handleClearSelections} />
      )}
      <_GenericDropDownMenu
        menuItems={menuItems}
        buttonText={`${count} files (${getFileSize(size)}) selected`}
        active={count > 0}
      />
    </>
  )
}

const getSelectedCount = (versions: DataStateVersions) => {
  return Object.entries(versions).reduce<{
    count: number
    size: number
  }>(
    (acc, [id, version]) => {
      if (version?.selected) {
        acc.count++
        acc.size += version.size || 0
      }
      return acc
    },
    { count: 0, size: 0 }
  )
}

interface MenuItem {
  text: string
  icon: string
  onClick: () => void
}

interface _GenericDropDownMenuProps {
  menuItems: MenuItem[]
  buttonText: string
  active: boolean
}

export const _GenericDropDownMenu: React.FC<_GenericDropDownMenuProps> = ({
  menuItems,
  buttonText,
  active
}) => {
  return (
    <>
      <UncontrolledButtonDropdown direction="down" className="select-dropdown">
        <div className={`d-flex select-wrapper ${active ? 'active' : 'disabled'}`}>
          <div className="file-detail">
            <i
              className={'glyphicons glyphicons-check'}
              style={{ height: 'unset' }}
            ></i>
            {buttonText}
          </div>
          <DropdownToggle
            caret
            className={`select-dropdown-trigger ${active ? 'active' : 'disabled'}`}
            disabled={!active}
          ></DropdownToggle>
        </div>
        <DropdownMenu>
          {menuItems.map((item, index) => (
            <DropdownItem key={index} onClick={item.onClick}>
              <i style={{ height: 'unset' }} className={item.icon}></i>
              <span>{item.text}</span>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledButtonDropdown>
    </>
  )
}
